import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MdLocationOn, MdPeople, MdBusiness, MdAttachMoney } from 'react-icons/md';
import { useAuth } from '../../../Context/AuthContext';
import { toast } from 'react-toastify';
import { getProviderAnalytics, getProviderAnalyticsTrend, getProviderBenchmark } from "../../../Services/AnalyticsService";
import CustomDatePicker from "../../../Components/Common/CustomDatePicker";
import AnalyticsChatWidget from "../../../Components/Analytics/AnalyticsChatWidget";
import AnalyticsProviderCharts from "./AnalyticsProviderCharts";
import IncentiveReportSection from "../../../Components/Analytics/IncentiveReportSection";
import BackButton from '../../../Components/Common/BackButton';

const resolveOrganizationId = (decodedToken) => {
    const possibleValues = [
        decodedToken?.organization_id,
        decodedToken?.OrganizationId,
        decodedToken?.organizationId,
        decodedToken?.org_id,
    ];

    return possibleValues.find((value) => value !== undefined && value !== null && value !== "") || null;
};

// One stat tile of the hero banner: the icon box never shrinks, and a long value wraps instead of overflowing.
function HeroStat({ icon, label, value }) {
    return (
        <div className="h-100 d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <div
                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}
            >
                {icon}
            </div>
            <div className="min-w-0">
                <p className="mb-1 small text-white lh-sm">{label}</p>
                <p className="fw-bold mb-0 text-white text-break lh-sm" style={{ fontSize: 'clamp(1rem, 1.6vw, 1.15rem)' }}>{value}</p>
            </div>
        </div>
    );
}

const GRANULARITIES = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
];

export default function AnalyticsProductivityView() {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const { token, decodedToken } = useAuth();

    const routeProvider = React.useMemo(() => location?.state?.provider || null, [location?.state]);

    const organizationId = React.useMemo(() => (
        params?.organization_id
        || location?.state?.organization_id
        || resolveOrganizationId(decodedToken)
    ), [params?.organization_id, location?.state, decodedToken]);

    const providerName = React.useMemo(() => (
        params?.provider_name ? decodeURIComponent(params.provider_name) : (routeProvider?.provider_name || '')
    ), [params?.provider_name, routeProvider]);

    const [provider, setProvider] = React.useState(routeProvider);
    const [trend, setTrend] = React.useState(null);
    const [benchmark, setBenchmark] = React.useState(null);
    const [analyticsLoading, setAnalyticsLoading] = React.useState(false);
    const [analyticsError, setAnalyticsError] = React.useState(null);
    const [granularity, setGranularity] = React.useState('month');

    const formatDateDisplay = (d) => {
        if (!d) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    };

    const defaultEndDate = new Date();
    const defaultStartDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);

    const [startDate, setStartDate] = React.useState(() => defaultStartDate);
    const [endDate, setEndDate] = React.useState(() => defaultEndDate);

    const loadProviderAnalytics = React.useCallback(async (start = startDate, end = endDate, grain = granularity) => {
        if (!token || !organizationId || !providerName) return;
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        try {
            const s = start ? formatDateDisplay(start instanceof Date ? start : new Date(start)) : '';
            const e = end ? formatDateDisplay(end instanceof Date ? end : new Date(end)) : '';
            const [providerRes, trendRes, benchmarkRes] = await Promise.all([
                getProviderAnalytics(token, organizationId, providerName, s, e),
                getProviderAnalyticsTrend(token, organizationId, providerName, s, e, grain),
                getProviderBenchmark(token, organizationId, providerName, s, e),
            ]);

            if (providerRes?.is_success === false) throw new Error(providerRes?.message || 'Failed to load provider analytics');

            setProvider(providerRes?.data || null);
            setTrend(trendRes?.data || null);
            setBenchmark(benchmarkRes?.data || null);

            if (!providerRes?.data) {
                toast.error('No productivity data found for this provider in the selected range.');
            }
        } catch (err) {
            console.error(err);
            setProvider(null);
            setTrend(null);
            setBenchmark(null);
            setAnalyticsError(err);
            toast.error('Unable to load provider analytics');
        } finally {
            setAnalyticsLoading(false);
        }
    }, [token, organizationId, providerName, startDate, endDate, granularity]);

    React.useEffect(() => {
        loadProviderAnalytics(startDate, endDate, granularity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, granularity, token, organizationId, providerName]);

    const facilities = provider?.facilities || [];

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                <BackButton onClick={() => navigate(-1)} />

                <AnalyticsChatWidget
                    organizationId={organizationId}
                />
            </div>

            <div className="p-3 p-sm-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                {/* Side by side only on very wide screens; below that the snapshot sits under the totals at full width. */}
                <div className="row g-4 align-items-stretch position-relative">
                    <div className="col-12 col-xxl-8">
                        <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 mb-3">
                            <span className="px-3 py-2 rounded-pill fw-semibold bg-success text-white" style={{ fontSize: '0.82rem' }}>
                                Provider Analytics
                            </span>
                            <span className="px-3 py-2 rounded-pill fw-semibold bg-info text-white" style={{ fontSize: '0.75rem' }}>
                                {(() => {
                                    if (!startDate && !endDate) return 'All Time';
                                    if (startDate && endDate) return `${formatDateDisplay(startDate)} → ${formatDateDisplay(endDate)}`;
                                    return startDate ? formatDateDisplay(startDate) : formatDateDisplay(endDate) || 'All Time';
                                })()}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-3 text-break" style={{ fontSize: 'clamp(1.35rem, 2.2vw, 2rem)' }}>
                            {provider?.provider_name || providerName || 'Provider Analytics'}
                        </h2>
                        <div className="row g-3">
                            <div className="col-12 col-sm-6 col-lg-4">
                                <HeroStat icon={<MdPeople size={22} color="#16a34a" />} label="Total Patients" value={(provider?.totals?.patient_count ?? 0).toLocaleString()} />
                            </div>
                            <div className="col-12 col-sm-6 col-lg-4">
                                <HeroStat icon={<MdBusiness size={22} color="#0d6efd" />} label="Total Visits" value={(provider?.totals?.visit_count ?? 0).toLocaleString()} />
                            </div>
                            <div className="col-12 col-lg-4">
                                <HeroStat icon={<MdAttachMoney size={22} color="#f59e0b" />} label="Total Revenue" value={`$${(provider?.revenue?.total ?? 0).toLocaleString()}`} />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xxl-4">
                        <div className="h-100 p-3 rounded-4 d-flex flex-column" style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}>
                            <div className="mb-3">
                                <p className="mb-1 small" style={{ color: 'rgba(255,255,255,0.65)' }}>Productivity analytics overview</p>
                                <p className="fw-bold text-white mb-0" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>Snapshot</p>
                            </div>

                            <div className="row g-3 flex-grow-1 align-content-center">
                                <div className="col-12 col-sm-6">
                                    <HeroStat icon={<MdLocationOn size={22} className="hc-text-primary" />} label="Facilities" value={facilities.length} />
                                </div>
                                <div className="col-12 col-sm-6">
                                    <HeroStat icon={<MdPeople size={22} color="#16a34a" />} label="Productivity Score" value={`${provider?.productivity_score?.score ?? 0}%`} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-3 p-sm-4 p-lg-5 hc-surface hc-card-primary">

                        <div className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4">
                            <h5 className="fw-bold mb-0 d-flex flex-wrap align-items-center gap-2 hc-text-primary">
                                <MdLocationOn size={20} />
                                Facilities
                            </h5>

                            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">

                                <CustomDatePicker
                                    id="analytics-start-date"
                                    value={startDate}
                                    onChange={(value) =>
                                        setStartDate(
                                            value ? new Date(value + "T00:00:00") : null
                                        )
                                    }
                                />

                                <CustomDatePicker
                                    id="analytics-end-date"
                                    value={endDate}
                                    onChange={(value) =>
                                        setEndDate(
                                            value ? new Date(value + "T00:00:00") : null
                                        )
                                    }/>

                                <button
                                    className="btn btn-md bg-danger text-white fw-semibold"
                                    style={{
                                        height: "38px",
                                        borderRadius: "8px"
                                    }}
                                    onClick={() => {
                                        setStartDate(defaultStartDate);
                                        setEndDate(defaultEndDate);
                                        setGranularity('month');
                                        loadProviderAnalytics(defaultStartDate, defaultEndDate, 'month');
                                    }}>
                                    Reset
                                </button>

                            </div>

                        </div>

                        <div className="row g-3">
                            {analyticsLoading ? (
                                Array.from({ length: 3 }).map((_, idx) => (
                                    <div className="col-12 col-md-6 col-xl-4" key={idx}>
                                        <div
                                            className="service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 hc-text-primary placeholder-glow"
                                            style={{ minHeight: '72px', display: 'flex', alignItems: 'center', cursor: 'default' }}
                                        >
                                            <div className="w-100">
                                                <div className="small placeholder w-75 rounded mb-2 d-block"></div>
                                                <div className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                                    <span className="placeholder w-50 rounded"></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : !facilities.length ? (
                                <div className="col-12">
                                    <p className="mb-0 text-muted">No facilities found for this provider in the selected range.</p>
                                </div>
                            ) : facilities.map((f) => (
                                <div className="col-12 col-md-6 col-xl-4" key={f.facility_name}>
                                    <div
                                        className="service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 hc-text-primary"
                                        style={{ minHeight: '72px', display: 'flex', alignItems: 'center', cursor: 'default' }}
                                    >
                                        <div className="min-w-0">
                                            <div className="small text-break">{f.facility_name}</div>
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                {f.patient_count} patients · {f.visit_count} visits
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-3 p-sm-4 p-lg-5 hc-surface hc-card-primary">
                        <IncentiveReportSection
                            token={token}
                            organizationId={organizationId}
                            providerName={provider?.provider_name || providerName}
                            startDate={startDate}
                            endDate={endDate}
                            facilities={facilities}
                        />
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-12">
                    <div className="p-3 p-sm-4 p-lg-5 hc-surface hc-card-primary">
                        <AnalyticsProviderCharts provider={provider} trend={trend} benchmark={benchmark} loading={analyticsLoading} error={analyticsError} />
                    </div>
                </div>
            </div>
        </div>
    );
}
